// =====================================================================
// src/screens/v2/OrderDetail.jsx
//
// V2 Order Detail screen.
//
// Shows a single order: status hero, timeline, business card, items,
// summary, delivery info, notes, payment status, platform disclaimer.
//
// Status flow: placed → confirmed → preparing → ready → completed
//
// In Phase B, MOCK order data will be replaced by Firestore reads on
// the `orders` collection by order ID, with realtime status updates.
//
// Props:
//   orderId      — which order to show (used as display ID, content is mocked)
//   onBack       — return to previous screen (Cart confirmation or Connect List)
//   onOpenChat   — open chat with the business
//   onOpenBusiness — navigate to business profile
// =====================================================================

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, MessageSquare, MapPin, Phone,
  Check, Clock, RefreshCw, Info, X, Loader2
} from 'lucide-react';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../lib/firebase';
import {
  getBusiness,
  logoBgFromCategory,
  categoryLabelFromId,
} from '../../lib/v2/firestore';
import './OrderDetail.css';
import BusinessLogo from '../../components/BusinessLogo';

// ============================================================
// HELPERS
// ============================================================

// Category → emoji for the business logo (we don't have category-level
// images in the seed; this gives a reasonable visual cue).
const CATEGORY_EMOJI = {
  food_beverages: '🍯',
  apparel: '👗',
  services: '🔧',
  groceries: '🛒',
  electronics: '📱',
  health_beauty: '💄',
  home_decor: '🏡',
};
function emojiForCategory(cat) {
  return CATEGORY_EMOJI[cat] || '🏪';
}

// Format Firestore Timestamp (or Date or ISO string) as "2:34 PM".
function formatTime(ts) {
  if (!ts) return '';
  try {
    let d;
    if (ts.toDate) d = ts.toDate();
    else if (ts.toMillis) d = new Date(ts.toMillis());
    else if (typeof ts === 'string') d = new Date(ts);
    else d = new Date(ts);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-IN', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

// ============================================================
// STATUS CONFIG
// ============================================================
//
// timelineStep: 0..4 for normal progression; -1 for cancelled (no
// timeline rendering).

const STATUS_CONFIG = {
  placed: {
    icon: '⏳',
    title: 'Order placed',
    subtitle: 'has been notified. They\'ll confirm shortly and share payment details via chat.',
    timelineStep: 0,
  },
  confirmed: {
    icon: '✓',
    title: 'Order confirmed',
    subtitle: 'has confirmed your order. They\'ll start preparing it soon.',
    timelineStep: 1,
  },
  preparing: {
    icon: '👨‍🍳',
    title: 'Being prepared',
    subtitle: 'is preparing your order right now.',
    timelineStep: 2,
  },
  ready: {
    icon: '🛵',
    title: 'Out for delivery',
    subtitle: 'has dispatched your order. It\'ll arrive shortly.',
    timelineStep: 3,
  },
  completed: {
    icon: '🎉',
    title: 'Delivered',
    subtitle: 'Order completed. Hope you enjoyed it!',
    timelineStep: 4,
  },
  cancelled: {
    icon: '✕',
    title: 'Order cancelled',
    subtitle: 'has cancelled this order. See details below.',
    timelineStep: -1,
  },
};

// Path Z Commit 2: merchant-voiced subtitles. Rendered when the
// viewer is the business owner (e.g. opening OrderDetail from chat).
// Different copy per status — keeps the merchant focused on what's
// next (mark preparing / dispatch / etc.) rather than narrating
// what the business has done from the customer's POV. Title from
// STATUS_CONFIG is reused as-is — it's already neutral.
//
// Subtitle is rendered WITHOUT the businessName prefix in merchant
// mode (it would read 'Test Business You confirmed...' otherwise).
// The viewer-mode branch in the JSX handles this.
const MERCHANT_STATUS_SUBTITLES = {
  placed: 'New order from the customer. Confirm or cancel within a few minutes.',
  confirmed: 'You confirmed this order. Mark it preparing once you\'ve started.',
  preparing: 'You\'re preparing this order. Mark ready when packed for handoff.',
  ready: 'Order dispatched. Mark complete once the customer receives it.',
  completed: 'Order complete. Thanks for serving the community.',
  cancelled: 'This order was cancelled. See details below.',
};

const TIMELINE_STEPS = [
  { key: 'placed',     label: 'Placed' },
  { key: 'confirmed',  label: 'Confirmed' },
  { key: 'preparing',  label: 'Preparing' },
  { key: 'ready',      label: 'Out for delivery' },
  { key: 'completed',  label: 'Delivered' },
];

// ============================================================
// COMPONENT
// ============================================================

export default function OrderDetail({ orderId, onBack, onOpenChat, onOpenBusiness }) {
  // ============================================================
  // STATE — order doc (live), business doc (one-shot), UI flags
  // ============================================================
  const [orderDoc, setOrderDoc] = useState(null);
  const [orderError, setOrderError] = useState(null);    // 'not_found' | 'permission' | 'other'
  const [loadingOrder, setLoadingOrder] = useState(true);

  const [businessDoc, setBusinessDoc] = useState(null);
  const [loadingBusiness, setLoadingBusiness] = useState(true);

  // Path Z Commit 2: track viewer's uid so OrderDetail can self-detect
  // whether the current user is the customer (default) or the merchant
  // (business owner). Mirrors MerchantApp's onAuthStateChanged pattern
  // — auth resolution can be async on cold start, so we subscribe
  // rather than reading auth.currentUser synchronously.
  const [viewerUid, setViewerUid] = useState(() => auth.currentUser?.uid || null);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setViewerUid(u?.uid || null);
    });
    return () => unsub();
  }, []);

  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // ============================================================
  // EFFECT: Subscribe to the order doc.
  // Live updates let the timeline animate forward as the business
  // progresses the order through its state machine. Also catches the
  // initial 'placed' → 'confirmed'/'cancelled' flip from the Cloud
  // Function if the user lands here within ~5s of placing the order.
  // ============================================================
  useEffect(() => {
    if (!orderId) {
      setLoadingOrder(false);
      setOrderError('not_found');
      return undefined;
    }
    setLoadingOrder(true);
    setOrderError(null);
    const ref = doc(db, 'orders', orderId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setOrderError('not_found');
          setOrderDoc(null);
          setLoadingOrder(false);
          return;
        }
        setOrderDoc({ id: snap.id, ...snap.data() });
        setLoadingOrder(false);
      },
      (err) => {
        console.error('[OrderDetail] order subscription failed:', err);
        setOrderError(err.code === 'permission-denied' ? 'permission' : 'other');
        setLoadingOrder(false);
      }
    );
    return () => unsub();
  }, [orderId]);

  // ============================================================
  // EFFECT: Fetch the business doc once we know the order's
  // businessId. One-shot — business name/category don't change
  // mid-order, so a subscription would just waste reads.
  // ============================================================
  useEffect(() => {
    const bid = orderDoc?.businessId;
    if (!bid) {
      setLoadingBusiness(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingBusiness(true);
        const b = await getBusiness(bid);
        if (cancelled) return;
        setBusinessDoc(b);
      } catch (err) {
        console.warn('[OrderDetail] business fetch failed:', err);
        if (!cancelled) setBusinessDoc(null);
      } finally {
        if (!cancelled) setLoadingBusiness(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderDoc?.businessId]);

  // ============================================================
  // EARLY RETURNS — skeleton + error states
  // ============================================================
  if (loadingOrder || loadingBusiness) {
    return <OrderDetailSkeleton onBack={onBack} />;
  }
  if (orderError === 'not_found') {
    return (
      <OrderDetailError
        title="Order not found"
        message="This order may have been deleted, or the link is incorrect."
        onBack={onBack}
      />
    );
  }
  if (orderError === 'permission') {
    return (
      <OrderDetailError
        title="Can't view this order"
        message="You don't have access to this order. Sign in as the customer who placed it."
        onBack={onBack}
      />
    );
  }
  if (orderError) {
    return (
      <OrderDetailError
        title="Something went wrong"
        message="We couldn't load this order. Please check your connection and try again."
        onBack={onBack}
      />
    );
  }

  // ============================================================
  // MAP FIRESTORE DOC TO THE SHAPE THE JSX BELOW EXPECTS
  // (matches the previous MOCK_ORDER shape exactly so we keep
  //  almost all of the render code as-is)
  // ============================================================
  const businessName = businessDoc?.name || 'Business';
  const businessCategory = businessDoc?.category || '';
  const businessLocality = businessDoc?.locality || businessDoc?.city || '';
  const businessVerified = businessDoc?.status === 'verified';

  const order = {
    businessId: orderDoc.businessId,
    businessName,
    businessLogo: emojiForCategory(businessCategory),
    businessLogoBg: logoBgFromCategory(businessCategory),
    // Phase 2.3 C2.5 — uploaded logo URL from the merchant's business doc.
    businessLogoImageUrl: businessDoc?.logoImageUrl || null,
    businessVerified,
    businessMeta: [
      categoryLabelFromId(businessCategory),
      businessLocality,
    ].filter(Boolean).join(' · '),

    status: orderDoc.status || 'placed',
    mode: orderDoc.mode || 'pickup',
    displayNumber: orderDoc.displayNumber || orderId,

    // Format timestamps to "2:34 PM" strings for the timeline labels
    placedAt: formatTime(orderDoc.placedAt),
    confirmedAt: formatTime(orderDoc.confirmedAt),
    preparingAt: formatTime(orderDoc.preparingAt),
    readyAt: formatTime(orderDoc.readyAt),
    completedAt: formatTime(orderDoc.completedAt),
    cancelledAt: formatTime(orderDoc.cancelledAt),

    // Items: real order doc has [{ productId, name, price, quantity }]
    // The JSX below expects id, name, qty, price, image, imageBg.
    // Phase 2.3 C3 — Cart snapshots imageUrl onto each order item at
    // creation time (so the photo is frozen with the order even if the
    // merchant later changes the product). Read it here; fall back to
    // the category emoji when an item has no imageUrl (legacy orders
    // and products without uploaded photos).
    items: (orderDoc.items || []).map((it, idx) => ({
      id: it.productId || `item-${idx}`,
      name: it.name || it.productId || 'Item',
      qty: it.quantity || 1,
      price: it.price || 0,
      image: emojiForCategory(businessCategory),
      imageBg: logoBgFromCategory(businessCategory),
      imageUrl: it.imageUrl || null,
    })),

    subtotal: orderDoc.subtotal || 0,
    deliveryFee: orderDoc.deliveryFee || 0,
    total: orderDoc.total || 0,
    deliverTo: orderDoc.deliveryAddress || '',
    notes: orderDoc.notes || '',

    // Cancellation context (only present when status === 'cancelled')
    cancelReason: orderDoc.cancelReason || '',
    validationFailures: orderDoc.validationFailures || [],

    // Payment status — derived. Once business confirms, they share
    // payment details in chat (Step 3.5c+ for the actual chat).
    paymentStatus:
      orderDoc.status === 'placed' || orderDoc.status === 'confirmed'
        ? 'awaiting'
        : orderDoc.status === 'cancelled'
          ? 'awaiting'
          : 'shared',
  };

  const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.placed;
  const currentStep = config.timelineStep;
  const isCompleted = order.status === 'completed';
  const isCancelled = order.status === 'cancelled';

  // Path Z Commit 2: viewer-mode detection.
  // - 'merchant' when the signed-in user owns the business → render
  //   merchant-voiced subtitle, hide customer CTA bar (merchant
  //   manages the order from MerchantApp, not from here).
  // - 'customer' (default) for the customer or any unauthenticated /
  //   non-owner viewer. Keeps existing copy + CTAs intact.
  // Detection uses the already-loaded businessDoc.ownerId — no
  // extra Firestore read needed.
  const viewerMode =
    viewerUid && businessDoc?.ownerId && viewerUid === businessDoc.ownerId
      ? 'merchant'
      : 'customer';
  const isMerchantViewer = viewerMode === 'merchant';

  // These use a proper if/else rather than `fn?.() || toast(...)`, which fires
  // the fallback even on success (see ProfileTabV2's note, and the bugs fixed
  // in Cart and OrderChat). The else branches are defensive — every current
  // call site passes both props — so the copy says "unavailable", not "coming
  // soon". Nothing is coming; the prop is simply missing.
  function handleChat() {
    if (onOpenChat) onOpenChat(order.id);
    else setToast('Chat is unavailable for this order');
  }

  function handleOpenBusiness() {
    if (onOpenBusiness) onOpenBusiness(order.businessId);
    else setToast('Business profile is unavailable');
  }

  function handleReorder() {
    // Stub-cleanup: previously a "coming in next checkpoint" toast.
    // Cart's local-state model means we can't programmatically pre-fill
    // items from a past order — the customer would re-pick them anyway.
    // Best UX is to route them to the business profile where products
    // live, same as the empty-cart "Add items" CTA.
    if (onOpenBusiness && order?.businessId) {
      onOpenBusiness(order.businessId);
    } else {
      setToast('Business profile unavailable');
    }
  }

  // Timeline progress bar fill — only meaningful for non-cancelled orders.
  // 5 steps → 4 gaps → 0/4, 1/4, 2/4, 3/4, 4/4 = 0%, 25%, 50%, 75%, 100%
  const progressPercent = isCancelled
    ? 0
    : (currentStep / (TIMELINE_STEPS.length - 1)) * 100;

  return (
    <div className="v2-order-detail">
      {/* ============ TOP BAR ============ */}
      <div className="v2od-top-bar">
        <button className="v2od-back-btn" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <div className="v2od-top-info">
          <div className="v2od-top-title">Order #{order.displayNumber}</div>
          <div className="v2od-top-sub">{order.businessName}</div>
        </div>
        <button
          className="v2od-icon-btn"
          onClick={handleChat}
          aria-label="Messages"
        >
          <MessageSquare size={18} />
          {/* Placeholder sweep: this badge was the literal string "2" — a mock
              unread count shown on every order ever placed. Nothing computed it.
              Removed rather than faked; a real count needs the per-order chat's
              unread state, which nothing on this screen subscribes to. */}
        </button>
        {/* Placeholder sweep: "More options" opened a toast and nothing else.
            Its natural contents (Report this order, Block) are contextual
            reporting — a planned session that gates V2 on Android. */}
      </div>

      {/* ============ CONTENT ============ */}
      <div className="v2od-content">
        {/* Status hero */}
        <div className={`v2od-status-hero v2od-status-${order.status}`}>
          <div className="v2od-status-icon">{config.icon}</div>
          <h1 className="v2od-status-title">{config.title}</h1>
          <p className="v2od-status-subtitle">
            {isMerchantViewer ? (
              // Path Z Commit 2: merchant-voiced subtitle. No
              // businessName prefix — merchant knows it's their own.
              MERCHANT_STATUS_SUBTITLES[order.status] ||
              MERCHANT_STATUS_SUBTITLES.placed
            ) : (
              <>
                <b>{order.businessName}</b> {config.subtitle}
              </>
            )}
          </p>
        </div>

        {/* Timeline — hidden for cancelled orders */}
        {!isCancelled && (
          <div className="v2od-timeline">
            <div className="v2od-timeline-track">
              <div className="v2od-timeline-progress" style={{ width: `${progressPercent}%` }} />
            </div>
            <div className="v2od-timeline-steps">
              {TIMELINE_STEPS.map((step, idx) => {
                const isActive = idx <= currentStep;
                const isCurrent = idx === currentStep;
                const timeStr = order[step.key + 'At'];
                return (
                  <div
                    key={step.key}
                    className={`v2od-timeline-step ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}
                  >
                    <div className="v2od-timeline-dot">
                      {isActive && idx < currentStep && <Check size={9} strokeWidth={3} />}
                    </div>
                    <div className="v2od-timeline-label">{step.label}</div>
                    {timeStr && <div className="v2od-timeline-time">{timeStr}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cancellation card — shown only when cancelled. Surfaces the
            structured failures the Cloud Function records for price
            tampering, missing products, out-of-stock, etc. */}
        {isCancelled && (
          <div className="v2od-section">
            <div className="v2od-section-label">Why was this cancelled?</div>
            <div className="v2od-info-block" style={{ borderColor: 'var(--danger, #C45A3F)' }}>
              <div className="v2od-info-row">
                <div className="v2od-info-icon"><X size={14} /></div>
                <div className="v2od-info-body">
                  <div className="v2od-info-label">
                    {order.cancelReason === 'price_validation_failed'
                      ? 'Price/availability mismatch'
                      : order.cancelReason || 'Cancelled'}
                    {order.cancelledAt ? ` · ${order.cancelledAt}` : ''}
                  </div>
                  {order.validationFailures.length > 0 && (
                    <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                      {order.validationFailures.map((f, i) => (
                        <li key={i} className="v2od-info-value">
                          {f.reason === 'price_mismatch' && (
                            <>Price for <b>{f.productId}</b> changed from ₹{f.submittedPrice} to ₹{f.actualPrice}.</>
                          )}
                          {f.reason === 'product_not_found' && (
                            <>Product <b>{f.productId}</b> is no longer available.</>
                          )}
                          {f.reason === 'out_of_stock' && (
                            <>Product <b>{f.productId}</b> is out of stock.</>
                          )}
                          {!['price_mismatch','product_not_found','out_of_stock'].includes(f.reason) && (
                            <>{f.reason}{f.productId ? ` (${f.productId})` : ''}</>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Business */}
        <div className="v2od-section">
          <div className="v2od-section-label">Business</div>
          <div className="v2od-biz-card" onClick={handleOpenBusiness}>
            <BusinessLogo
              className="v2od-biz-logo"
              imageUrl={order.businessLogoImageUrl}
              emoji={order.businessLogo}
              bg={order.businessLogoBg}
            />
            <div className="v2od-biz-info">
              <div className="v2od-biz-name-row">
                <span className="v2od-biz-name">{order.businessName}</span>
                {order.businessVerified && (
                  <span className="v2od-verified">
                    <Check size={9} strokeWidth={3} />
                  </span>
                )}
              </div>
              <div className="v2od-biz-meta">{order.businessMeta}</div>
            </div>
            <button
              className="v2od-biz-chat-btn"
              onClick={e => { e.stopPropagation(); handleChat(); }}
            >
              <MessageSquare size={11} /> Chat
            </button>
          </div>
        </div>

        {/* Items */}
        <div className="v2od-section">
          <div className="v2od-section-label">
            Items <span className="v2od-section-count">{order.items.length}</span>
          </div>
          <div className="v2od-items-list">
            {order.items.map(item => (
              <div key={item.id} className="v2od-item">
                <div
                  className="v2od-item-image"
                  style={{
                    background: item.imageUrl ? 'transparent' : item.imageBg,
                    overflow: 'hidden',
                  }}
                >
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 'inherit',
                        display: 'block',
                      }}
                    />
                  ) : (
                    item.image
                  )}
                </div>
                <div className="v2od-item-body">
                  <div className="v2od-item-name">{item.name}</div>
                  <div className="v2od-item-qty">Qty {item.qty} · ₹{item.price} each</div>
                </div>
                <div className="v2od-item-price">₹{(item.price * item.qty).toLocaleString('en-IN')}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="v2od-section">
          <div className="v2od-section-label">Order summary</div>
          <div className="v2od-summary">
            <div className="v2od-summary-row">
              <span>Subtotal</span>
              <span className="v2od-summary-value">₹{order.subtotal.toLocaleString('en-IN')}</span>
            </div>
            {order.mode === 'delivery' && (
              <div className="v2od-summary-row">
                <span>Delivery fee</span>
                <span className="v2od-summary-value">₹{order.deliveryFee}</span>
              </div>
            )}
            <div className="v2od-summary-divider" />
            <div className="v2od-summary-row v2od-summary-total">
              <span>Total</span>
              <span className="v2od-summary-value">₹{order.total.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        {/* Delivery / Pickup info */}
        {order.mode === 'delivery' && order.deliverTo && (
          <div className="v2od-section">
            <div className="v2od-section-label">Delivery details</div>
            <div className="v2od-info-block">
              <div className="v2od-info-row">
                <div className="v2od-info-icon"><MapPin size={14} /></div>
                <div className="v2od-info-body">
                  <div className="v2od-info-label">Deliver to</div>
                  <div className="v2od-info-value">{order.deliverTo}</div>
                </div>
              </div>
            </div>
          </div>
        )}
        {order.mode === 'pickup' && (
          <div className="v2od-section">
            <div className="v2od-section-label">Pickup details</div>
            <div className="v2od-info-block">
              <div className="v2od-info-row">
                <div className="v2od-info-icon"><MapPin size={14} /></div>
                <div className="v2od-info-body">
                  <div className="v2od-info-label">Pick up from</div>
                  <div className="v2od-info-value">
                    {businessDoc?.address || businessDoc?.locality || businessDoc?.city || 'See business profile for address'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        {order.notes && (
          <div className="v2od-section">
            <div className="v2od-section-label">Your note to the business</div>
            <div className="v2od-notes-block">{order.notes}</div>
          </div>
        )}

        {/* Payment status */}
        <div className="v2od-section">
          <div className="v2od-section-label">Payment</div>
          <div className="v2od-payment-status">
            <span className="v2od-payment-icon">
              {order.paymentStatus === 'paid' ? '✓' : order.paymentStatus === 'shared' ? '💬' : '⏳'}
            </span>
            <div className="v2od-payment-body">
              <div className="v2od-payment-title">
                {order.paymentStatus === 'awaiting' && 'Awaiting payment details from business'}
                {order.paymentStatus === 'shared' && 'Payment details shared in chat'}
                {order.paymentStatus === 'paid' && 'Payment completed'}
              </div>
              <div className="v2od-payment-text">
                Once {order.businessName} confirms your order, they'll share{' '}
                <b>payment details (UPI / cash / link)</b> in chat. Aangan doesn't process payments.
              </div>
            </div>
          </div>
        </div>

        {/* Platform disclaimer */}
        <div className="v2od-section">
          <div className="v2od-platform-footer">
            <div className="v2od-platform-header">
              <Info size={12} /> About Aangan's role
            </div>
            <div className="v2od-platform-text">
              Aangan connects you with this business. The business handles preparation,
              delivery, payment, and refunds. For order issues, chat with the business.
              For platform issues, email{' '}
              <a href="mailto:aangansupport@gmail.com">aangansupport@gmail.com</a>.
            </div>
          </div>
        </div>
      </div>

      {/* ============ STICKY BOTTOM CTA ============ */}
      {/* Path Z Commit 2: hidden in merchant mode. Merchant came here
          from chat to glance at order details; status transitions are
          handled in MerchantApp's Orders tab. Back returns to chat. */}
      {!isMerchantViewer && (
        <div className="v2od-bottom-cta">
          <button className="v2od-cta-btn" onClick={handleChat}>
            <MessageSquare size={14} /> Chat with business
          </button>
          <button
            className="v2od-cta-btn primary"
            onClick={isCompleted ? handleReorder : handleOpenBusiness}
          >
            {isCompleted ? <><RefreshCw size={14} /> Reorder</> : <>View profile</>}
          </button>
        </div>
      )}

      {/* ============ TOAST ============ */}
      {toast && <div className="v2od-toast">{toast}</div>}
    </div>
  );
}

// ============================================================
// SUB-COMPONENT: Skeleton (shown while order/business are loading)
// Reuses existing v2od- classes where possible; pulse animation
// can be added in OrderDetail.css later.
// ============================================================
function OrderDetailSkeleton({ onBack }) {
  return (
    <div className="v2-order-detail">
      <div className="v2od-top-bar">
        <button className="v2od-back-btn" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <div className="v2od-top-info">
          <div className="v2od-top-title" style={{ opacity: 0.5 }}>Loading order…</div>
          <div className="v2od-top-sub" style={{ opacity: 0.4 }}>Just a moment</div>
        </div>
      </div>
      <div className="v2od-content">
        <div className="v2od-status-hero" style={{ opacity: 0.6 }}>
          <div className="v2od-status-icon">
            <Loader2 size={28} className="v2od-spinner" />
          </div>
          <h1 className="v2od-status-title">Loading…</h1>
          <p className="v2od-status-subtitle">Fetching your order details</p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SUB-COMPONENT: Error (order not found / permission / other)
// ============================================================
function OrderDetailError({ title, message, onBack }) {
  return (
    <div className="v2-order-detail">
      <div className="v2od-top-bar">
        <button className="v2od-back-btn" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <div className="v2od-top-info">
          <div className="v2od-top-title">{title}</div>
        </div>
      </div>
      <div className="v2od-content">
        <div className="v2od-status-hero">
          <div className="v2od-status-icon"><X size={28} /></div>
          <h1 className="v2od-status-title">{title}</h1>
          <p className="v2od-status-subtitle">{message}</p>
        </div>
      </div>
    </div>
  );
}
